// Глобальный store настроек + данные пользователя Telegram. Persist в localStorage.
// Спроектирован под масштаб: легко добавить новые флаги/разделы (рефералка, уведомления).
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

function getTgUser() {
  try {
    const u = window.Telegram && window.Telegram.WebApp
      && window.Telegram.WebApp.initDataUnsafe && window.Telegram.WebApp.initDataUnsafe.user;
    return u || null;
  } catch (e) { return null; }
}
function lsGet(key, def) {
  try { const v = localStorage.getItem(key); return v == null ? def : JSON.parse(v); } catch (e) { return def; }
}
function lsSet(key, v) { try { localStorage.setItem(key, JSON.stringify(v)); } catch (e) {} }

function detectTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch (e) { return 'UTC'; }
}

// Часто используемые часовые пояса (можно расширять без переделок)
export const TIMEZONES = [
  { tz: 'auto', label: 'Auto' },
  // Russia (all 11 zones)
  { tz: 'Europe/Kaliningrad', label: 'Kaliningrad (UTC+2)' },
  { tz: 'Europe/Moscow', label: 'Moscow (UTC+3)' },
  { tz: 'Europe/Samara', label: 'Samara (UTC+4)' },
  { tz: 'Asia/Yekaterinburg', label: 'Yekaterinburg (UTC+5)' },
  { tz: 'Asia/Omsk', label: 'Omsk (UTC+6)' },
  { tz: 'Asia/Krasnoyarsk', label: 'Krasnoyarsk (UTC+7)' },
  { tz: 'Asia/Irkutsk', label: 'Irkutsk (UTC+8)' },
  { tz: 'Asia/Yakutsk', label: 'Yakutsk (UTC+9)' },
  { tz: 'Asia/Vladivostok', label: 'Vladivostok (UTC+10)' },
  { tz: 'Asia/Magadan', label: 'Magadan (UTC+11)' },
  { tz: 'Asia/Kamchatka', label: 'Kamchatka (UTC+12)' },
  // CIS / nearby
  { tz: 'Europe/Kyiv', label: 'Kyiv (UTC+2)' },
  { tz: 'Europe/Minsk', label: 'Minsk (UTC+3)' },
  { tz: 'Asia/Almaty', label: 'Almaty (UTC+5)' },
  { tz: 'Asia/Tashkent', label: 'Tashkent (UTC+5)' },
  { tz: 'Asia/Tbilisi', label: 'Tbilisi (UTC+4)' },
  { tz: 'Asia/Baku', label: 'Baku (UTC+4)' },
];

const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
  const [user] = useState(getTgUser);
  const [avatarReady, setAvatarReady] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [hideBalance, setHideBalanceS] = useState(() => lsGet('set.hideBalance', false));
  const [hideSmall, setHideSmallS] = useState(() => lsGet('set.hideSmall', false));
  const [timezone, setTimezoneS] = useState(() => lsGet('set.timezone', 'auto'));
  // Управление уведомлениями (вкл/выкл) — задел под бэкенд/бота
  const [notifDeposit, setNotifDepositS] = useState(() => lsGet('set.notifDeposit', true));
  const [notifWithdraw, setNotifWithdrawS] = useState(() => lsGet('set.notifWithdraw', true));

  // Предзагрузка аватара СРАЗУ (во время общей загрузки приложения)
  useEffect(() => {
    const url = user && user.photo_url;
    if (!url) { setAvatarFailed(true); return; }
    const img = new Image();
    img.onload = () => setAvatarReady(true);
    img.onerror = () => setAvatarFailed(true);
    img.src = url;
  }, [user]);

  const setHideBalance = useCallback((v) => setHideBalanceS((p) => { const nv = typeof v === 'function' ? v(p) : v; lsSet('set.hideBalance', nv); return nv; }), []);
  const setHideSmall = useCallback((v) => setHideSmallS((p) => { const nv = typeof v === 'function' ? v(p) : v; lsSet('set.hideSmall', nv); return nv; }), []);
  const setTimezone = useCallback((v) => { setTimezoneS(v); lsSet('set.timezone', v); }, []);
  const setNotifDeposit = useCallback((v) => setNotifDepositS((p) => { const nv = typeof v === 'function' ? v(p) : v; lsSet('set.notifDeposit', nv); return nv; }), []);
  const setNotifWithdraw = useCallback((v) => setNotifWithdrawS((p) => { const nv = typeof v === 'function' ? v(p) : v; lsSet('set.notifWithdraw', nv); return nv; }), []);

  const first = (user && (user.first_name || '')) || '';
  const last = (user && (user.last_name || '')) || '';
  const displayName = ([first, last].filter(Boolean).join(' ') || (user && user.username) || (user ? `ID ${user.id}` : 'Guest'));
  const initial = (displayName || 'U').trim().charAt(0).toUpperCase();
  const effectiveTz = timezone === 'auto' ? detectTimezone() : timezone;

  const value = {
    user,
    userId: user ? String(user.id) : null,
    username: user && user.username ? '@' + user.username : null,
    displayName, initial,
    avatarUrl: (user && user.photo_url) || '',
    avatarReady, avatarFailed,
    hideBalance, setHideBalance,
    hideSmall, setHideSmall,
    timezone, setTimezone, effectiveTz,
    notifDeposit, setNotifDeposit, notifWithdraw, setNotifWithdraw,
  };
  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings() {
  return useContext(SettingsContext) || {};
}
